'use client'

import { validateMultipleCustomDataFields } from '@repo/validation/customDataFieldValidation'
import { ExtensionsWizardForm } from '@repo/custom-data-pages/ExtensionsWizardForm'
import { LargeCustomDataPageIcon } from '@repo/ui/LargeCustomDataPageIcon'
import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useState } from 'react'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardPanel } from '@repo/ui/WizardPanel'
import PropTypes from 'prop-types'

export const NewClientExtensions = ({ updateStepState, wideMode, handlePrevNext, testData }) => {
  const {
    namedClients,
    extensionFields: fields,
    extensionValues: values,
    customDataPageFieldValues
  } = useNewClientStore()
  const firstNamedClient = namedClients?.[0]
  const clientType = firstNamedClient?.clientType || 'I'
  const { applyCustomDataFieldDefaultsAndFormatting } = useCustomDataPageStore()
  const [validationErrors, setValidationErrors] = useState({})

  const getOverrideDefaultValues = () => {
    const overrideDefaultValues = {}
    customDataPageFieldValues?.forEach((item) => {
      overrideDefaultValues[item.customDataFieldId] = item.customDataFieldValue
    })
    return overrideDefaultValues
  }

  const overrideDefaultValues = getOverrideDefaultValues()

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (fields?.length) {
      const valuesWithDefaultsAndFormatting = applyCustomDataFieldDefaultsAndFormatting({ fields, values })

      const { allValidationErrors } = await validateMultipleCustomDataFields({
        fields,
        values: valuesWithDefaultsAndFormatting
      })

      if (Object.keys(allValidationErrors).length > 0) {
        console.log('==================== VALIDATION ERRORS')
        console.log(allValidationErrors)
        setValidationErrors(allValidationErrors)
        document.querySelector('[data-testid="WizardStepContentsContainer"]').scrollTo({ top: 0, behavior: 'smooth' })
        return false
      }

      const newCustomDataPageFieldValues = fields
        .map((field) => ({
          customDataPageId: field.pageId,
          customDataFieldId: field.fieldId,
          customDataFieldValue: valuesWithDefaultsAndFormatting[field.fieldId] || null
        }))
        .filter((item) => item.customDataFieldValue !== null)

      // we need to merge the custom data page field values from the store with the extensions values
      // the values in the store might have been pre-populated with default values, e.g. from a Third Party Notification
      const mergedCustomDataPageFieldValues = customDataPageFieldValues ? [...customDataPageFieldValues] : []

      newCustomDataPageFieldValues?.forEach((item) => {
        // if extension item was already defined in the store, override it with the extensions value
        const existingItemIndex = mergedCustomDataPageFieldValues.findIndex(
          (i) => i.customDataPageId === item.customDataPageId && i.customDataFieldId === item.customDataFieldId
        )
        if (existingItemIndex !== -1) {
          mergedCustomDataPageFieldValues[existingItemIndex] = item
        }
        // if the item is not included in the extensions, add it to the merged custom data page field values
        else {
          mergedCustomDataPageFieldValues.push(item)
        }
      })

      if (mergedCustomDataPageFieldValues?.length)
        useNewClientStore.setState({ customDataPageFieldValues: mergedCustomDataPageFieldValues })
    }

    updateStepState({ status: 'completed', statusText: 'Complete' })
    handlePrevNext(1)
  }

  const handleClickBack = () => {
    handlePrevNext(-1)
  }

  return (
    <>
      <form onSubmit={handleSubmit} data-testid="NewClientExtensions">
        <WizardPanel>
          <WizardFormSection>
            <WizardIconTitle title={'Client extensions'} icon={LargeCustomDataPageIcon} sx={{ mb: 8, width: '100%' }} />
            <ExtensionsWizardForm
              level="client"
              clientType={clientType}
              validationErrors={validationErrors}
              fields={fields}
              values={values}
              overrideDefaultValues={overrideDefaultValues}
              setFields={(extensionFields) => useNewClientStore.setState({ extensionFields })}
              setValues={(extensionValues) => useNewClientStore.setState({ extensionValues })}
              testData={testData}
            />
          </WizardFormSection>
        </WizardPanel>

        <WizardFooter wideMode={wideMode} continueButtonIsFormSubmitButton handleClickBack={handleClickBack} />
      </form>
    </>
  )
}

NewClientExtensions.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  changeWizard: PropTypes.func,
  testData: PropTypes.any
}
